<template>
  <div class="flex items-center gap-2.5 rounded-xl border border-default bg-elevated p-2.5">
    <span class="flex size-9 flex-none items-center justify-center rounded-full bg-primary text-xs font-bold text-on-primary shadow-sm">
      {{ sessionInitials }}
    </span>

    <div class="min-w-0 flex-1">
      <div class="truncate text-sm font-semibold text-highlighted">{{ sessionName }}</div>
      <div class="text-xs text-muted">{{ sessionRole }}</div>
      <button
        v-if="appStore.isLoggedIn()"
        type="button"
        class="text-xs font-semibold text-primary hover:underline"
        @click="appStore.requestChangePin()"
      >
        {{ t('session.change_pin') }}
      </button>
    </div>

    <UButton
      icon="i-lucide-lock"
      color="neutral"
      variant="outline"
      size="sm"
      :aria-label="t('session.lock')"
      @click="appStore.setLogout()"
    />
    <UButton
      :icon="colorModeIcon"
      color="neutral"
      variant="outline"
      size="sm"
      :aria-label="t('session.color_mode')"
      @click="cycleColorMode"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAppStore } from '@/stores/app'

const { t } = useI18n()
const appStore = useAppStore()

const sessionName = computed(() => appStore.identity?.name ?? t('session.not_logged_in'))
const sessionRole = computed(() => {
  if (!appStore.identity) return t('session.not_logged_in')
  return appStore.identity.is_admin ? t('session.administrator') : t('session.operator')
})
const sessionInitials = computed(() => {
  const name = appStore.identity?.name
  if (!name) return '—'
  const parts = name.trim().split(/\s+/)
  return parts
    .map((part, i) => (i === 0 || i === parts.length - 1 ? part[0] : ''))
    .join('')
    .toUpperCase()
})

const colorModeIcon = computed(() => {
  if (appStore.colorMode === 'dark') return 'i-lucide-moon'
  if (appStore.colorMode === 'light') return 'i-lucide-sun'
  return 'i-lucide-monitor'
})

const cycleColorMode = () => {
  const order = ['auto', 'light', 'dark'] as const
  const next = order[(order.indexOf(appStore.colorMode as (typeof order)[number]) + 1) % order.length]
  appStore.colorMode = next
}
</script>
